<template>
  <div class="install-select" :class="{ 'is-disabled': disabled, 'is-open': popupVisible }">
    <button type="button" class="select-trigger" :disabled="disabled" @click="openPopup">
      <span class="select-label">{{ label }}</span>
      <span class="select-value" :class="{ 'is-placeholder': !modelValue }">
        {{ selectedLabel || placeholder }}
      </span>
      <van-icon :name="popupVisible ? 'arrow-up' : 'arrow-down'" class="select-arrow" />
    </button>

    <Teleport to="body">
      <div v-if="popupVisible" class="select-overlay" @click="closePopup">
        <div class="select-popup" @click.stop>
          <div class="popup-header">
            <button type="button" class="popup-action" @click="closePopup">取消</button>
            <div class="popup-title">{{ title }}</div>
            <button
              type="button"
              class="popup-action popup-action-confirm"
              :disabled="!selectedValue"
              @click="confirmSelection"
            >
              确定
            </button>
          </div>

          <div v-if="filterable" class="popup-search">
            <input
              v-model.trim="keyword"
              class="search-input"
              type="text"
              :placeholder="searchPlaceholder"
            />
          </div>

          <div class="popup-options">
            <button
              v-for="option in filteredOptions"
              :key="option.value"
              type="button"
              class="popup-option"
              :class="{ 'is-selected': option.value === selectedValue }"
              @click="selectOption(option.value)"
            >
              <span class="option-text">{{ option.label }}</span>
              <van-icon v-if="option.value === selectedValue" name="success" class="option-check" size="20px" />
            </button>

            <div v-if="!filteredOptions.length" class="popup-empty">未找到匹配选项</div>
          </div>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'

const props = withDefaults(defineProps<{
  modelValue: string
  label: string
  title?: string
  placeholder?: string
  options: { value: string; label: string }[]
  disabled?: boolean
  filterable?: boolean
  searchPlaceholder?: string
}>(), {
  title: '',
  placeholder: '请选择',
  disabled: false,
  filterable: false,
  searchPlaceholder: '请输入关键字'
})

const emit = defineEmits<{
  'update:modelValue': [value: string]
  change: [value: string]
}>()

const popupVisible = ref(false)
const keyword = ref('')
const selectedValue = ref('')

const filteredOptions = computed(() => {
  const trimmedKeyword = keyword.value.trim().toLowerCase()

  if (!trimmedKeyword) {
    return props.options
  }

  return props.options.filter(option => option.label.toLowerCase().includes(trimmedKeyword))
})

const selectedLabel = computed(() => {
  return props.options.find(o => o.value === props.modelValue)?.label || ''
})

watch(() => props.modelValue, value => {
  selectedValue.value = value
}, { immediate: true })

watch(() => props.options, options => {
  if (selectedValue.value && !options.some(o => o.value === selectedValue.value)) {
    selectedValue.value = ''
    emit('update:modelValue', '')
    emit('change', '')
  }
})

const openPopup = async () => {
  if (props.disabled) {
    return
  }

  selectedValue.value = props.modelValue
  keyword.value = ''
  popupVisible.value = true
}

const closePopup = () => {
  popupVisible.value = false
  keyword.value = ''
}

const selectOption = (value: string) => {
  selectedValue.value = value
}

const confirmSelection = () => {
  if (!selectedValue.value) {
    return
  }

  emit('update:modelValue', selectedValue.value)
  emit('change', selectedValue.value)
  closePopup()
}
</script>

<style scoped>
.select-trigger {
  display: flex;
  align-items: center;
  width: 100%;
  padding: 16px;
  border: 0;
  border-radius: 12px;
  background: #f7f8fa;
  text-align: left;
}

.select-label {
  flex: none;
  font-size: 16px;
  color: #374151;
}

.select-value {
  min-width: 0;
  flex: 1;
  margin-left: 12px;
  font-size: 16px;
  text-align: right;
  word-break: break-all;
  color: #111827;
}

.select-value.is-placeholder {
  color: #9ca3af;
}

.select-arrow {
  margin-left: 8px;
  font-size: 14px;
  color: #9ca3af;
}

.is-disabled .select-trigger {
  opacity: 1;
  cursor: not-allowed;
}

.is-disabled .select-label,
.is-disabled .select-value,
.is-disabled .select-arrow {
  color: #9ca3af;
}

.select-overlay {
  position: fixed;
  inset: 0;
  z-index: 30;
  display: flex;
  align-items: flex-end;
  background: rgba(15, 23, 42, 0.35);
}

.select-popup {
  width: 100%;
  max-height: 75vh;
  padding-bottom: calc(env(safe-area-inset-bottom) + 12px);
  border-radius: 20px 20px 0 0;
  background: #fff;
}

.popup-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px;
  border-bottom: 1px solid #eef2f7;
}

.popup-title {
  font-size: 16px;
  font-weight: 600;
  color: #111827;
}

.popup-action {
  border: 0;
  background: transparent;
  font-size: 16px;
  color: #6b7280;
}

.popup-action-confirm {
  color: #3e87f8;
}

.popup-action-confirm:disabled {
  color: #cbd5e1;
}

.popup-search {
  padding: 12px 16px 0;
}

.search-input {
  width: 100%;
  padding: 12px 14px;
  border: 1px solid #dbe3ef;
  border-radius: 12px;
  box-sizing: border-box;
  font-size: 14px;
  outline: none;
  background: #f8fafc;
}

.search-input:focus {
  border-color: #3e87f8;
  background: #fff;
}

.popup-options {
  overflow-y: auto;
  max-height: calc(75vh - 120px);
  padding: 12px 16px 0;
}

.popup-option {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 14px 0;
  border: 0;
  border-bottom: 1px solid #eef2f7;
  background: transparent;
  text-align: left;
}

.popup-option:last-child {
  border-bottom: 0;
}

.option-text {
  font-size: 16px;
  color: #111827;
}

.popup-option.is-selected .option-text,
.option-check {
  color: #3e87f8;
}

.popup-empty {
  padding: 24px 0;
  font-size: 14px;
  text-align: center;
  color: #9ca3af;
}
</style>
